import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { AtSign, Plus, Trash2, Star, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SenderIdentitiesCard = ({ isAdmin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: '', from_name: '', from_email: '', tags: '' });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sender-identities`);
      setItems(res.data.identities || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.label.trim() || !form.from_name.trim() || !form.from_email.trim()) {
      toast.error('Label, from name and from email are required'); return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/sender-identities`, {
        label: form.label.trim(),
        from_name: form.from_name.trim(),
        from_email: form.from_email.trim(),
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      setForm({ label: '', from_name: '', from_email: '', tags: '' });
      toast.success('Sender identity added');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add identity');
    } finally { setAdding(false); }
  };

  const setDefault = async (id) => {
    try { await axios.put(`${API}/sender-identities/${id}`, { is_default: true }); load(); toast.success('Default sender updated'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/sender-identities/${id}`); load(); toast.success('Removed'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="sender-identities-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/10 rounded-lg">
            <AtSign className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <CardTitle className="text-lg text-white">Sender identities</CardTitle>
            <CardDescription>Multiple "from" addresses (e.g. Tech, Training). Campaigns auto-pick by tag, with a default fallback. Deliverability requires a verified Resend domain.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">No sender identities yet. Add one below to control the "from" address.</p>
        ) : (
          <div className="space-y-2">
            {items.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950 px-3 py-2" data-testid={`sender-identity-row-${i.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white truncate">{i.label}</span>
                    {i.is_default && <Badge variant="outline" className="text-[10px] border-violet-700/40 text-violet-400">Default</Badge>}
                  </div>
                  <p className="text-xs text-slate-400 truncate">{i.from_name} &lt;{i.from_email}&gt;</p>
                  {i.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {i.tags.map((t) => <span key={t} className="text-[10px] text-slate-400 bg-slate-800 rounded px-1.5 py-0.5">{t}</span>)}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    {!i.is_default && (
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-violet-400" onClick={() => setDefault(i.id)} title="Make default" data-testid={`sender-default-${i.id}`}>
                        <Star className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400" onClick={() => remove(i.id)} title="Delete" data-testid={`sender-delete-${i.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {isAdmin && (
          <div className="border-t border-slate-800 pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-xs">Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Tech" className="bg-slate-950 border-slate-800 mt-1" data-testid="sender-label-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">From name</Label>
              <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="CTG Tech" className="bg-slate-950 border-slate-800 mt-1" data-testid="sender-fromname-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">From email</Label>
              <Input value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} placeholder="tech@caretechguide.co.uk" className="bg-slate-950 border-slate-800 mt-1" data-testid="sender-fromemail-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Tags <span className="text-slate-500">(comma-separated)</span></Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="supplier, current_customer" className="bg-slate-950 border-slate-800 mt-1" data-testid="sender-tags-input" />
            </div>
            <div className="md:col-span-2">
              <Button onClick={add} disabled={adding} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="sender-add-btn">
                {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add sender identity
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
